import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useUpdateTimesheetRecord } from "@/hooks/useTimesheetRecords"
import { toast } from "sonner"
import type { RecordType } from "@/hooks/useTimesheetRecords"

interface EditTimesheetRecordModalProps {
  isOpen: boolean
  onClose: () => void
  record?: {
    id: string
    record_type: RecordType
    record_date: string
    record_time: string
    location?: string
    notes?: string
    employee?: {
      full_name: string
    }
  }
}

export const EditTimesheetRecordModal = ({ isOpen, onClose, record }: EditTimesheetRecordModalProps) => {
  const [recordType, setRecordType] = useState<RecordType>("entrada")
  const [recordDate, setRecordDate] = useState("")
  const [recordTime, setRecordTime] = useState("")
  const [location, setLocation] = useState("")
  const [notes, setNotes] = useState("")

  const updateRecord = useUpdateTimesheetRecord()

  useEffect(() => {
    if (record) {
      setRecordType(record.record_type)
      setRecordDate(record.record_date)
      setRecordTime(new Date(record.record_time).toLocaleTimeString('pt-BR', { 
        hour: '2-digit', 
        minute: '2-digit' 
      }))
      setLocation(record.location || "")
      setNotes(record.notes || "")
    }
  }, [record])

  const handleSave = async () => {
    if (!record) return

    try {
      // Combinar data e hora para criar o timestamp completo
      const [hours, minutes] = recordTime.split(':')
      const dateTime = new Date(recordDate)
      dateTime.setHours(parseInt(hours), parseInt(minutes))

      await updateRecord.mutateAsync({
        id: record.id,
        record_type: recordType,
        record_date: recordDate,
        record_time: dateTime.toISOString(),
        location: location || null,
        notes: notes || null
      })

      toast.success("Registro atualizado com sucesso!")
      onClose()
    } catch (error) {
      console.error("Erro ao atualizar registro:", error)
      toast.error("Erro ao atualizar registro")
    }
  }

  const handleClose = () => {
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            Editar Registro de Ponto - {record?.employee?.full_name}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="record_type">Tipo de Registro</Label>
            <Select value={recordType} onValueChange={(value: RecordType) => setRecordType(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione o tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="entrada">Entrada</SelectItem>
                <SelectItem value="saida_almoco">Saída Almoço</SelectItem>
                <SelectItem value="volta_almoco">Volta Almoço</SelectItem>
                <SelectItem value="saida">Saída</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="record_date">Data</Label>
            <Input
              id="record_date"
              type="date"
              value={recordDate}
              onChange={(e) => setRecordDate(e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="record_time">Horário</Label>
            <Input
              id="record_time"
              type="time"
              value={recordTime}
              onChange={(e) => setRecordTime(e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="location">Local (Opcional)</Label>
            <Input
              id="location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Ex: Portaria Principal"
            />
          </div>

          <div>
            <Label htmlFor="notes">Observações</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Observações sobre este registro..."
              rows={3}
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave}
              disabled={updateRecord.isPending}
            >
              {updateRecord.isPending ? "Salvando..." : "Salvar Alterações"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}